import { acceptedFileFormatTypes, AcceptedFileTypes, FileTypes } from '@ui';
import { z } from 'zod';
import { ATTACHMENT_MAX_FILE_SIZE_BYTES } from '@/constants/uploadLimits';

// Image File Schema

export interface FileSchemaOptions {
  maxFileSize?: number; // specify the size of the file in bytes
  minFiles?: number; // specify the minimum number of files to upload
  maxFiles?: number; // specify the maximum number of files to upload
  fileType?: FileTypes; // specify the type of the file (image, file, excel, spreadsheet, pdf, all)
  acceptedFormats?: AcceptedFileTypes[FileTypes]; // specify the accepted formats for the file
  required?: boolean;
}

export const fileSchemaHelper = 'Please select a file';
export const fileSchemaHelperOptional = `${fileSchemaHelper}, optional`;
export const createFileSchema = (options: FileSchemaOptions = {}) => {
  const {
    maxFileSize = ATTACHMENT_MAX_FILE_SIZE_BYTES, // 100 MB is the max size for a file as default
    minFiles = 0, // 0 is the default minimum number of files to upload
    maxFiles = undefined, // no maximum number of files to upload is default
    fileType = 'file',
    acceptedFormats,
    required = false,
  } = options;
  const acceptedFileTypes = acceptedFormats
    ? acceptedFormats
    : fileType
      ? acceptedFileFormatTypes[fileType]
      : ['*'];
  const formatBytes = (bytes: number, decimals = 2) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const dm = decimals < 0 ? 0 : decimals;
    const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i];
  };

  // Check if multiple files are expected
  // Use array schema if maxFiles > 1 or minFiles > 1 (allowing multiple files)
  const isMultipleFiles =
    (maxFiles !== undefined && maxFiles > 1) ||
    (minFiles !== undefined && minFiles > 1);

  // Helper function to validate a single file
  const validateFile = (file: File): boolean => {
    // Validate file size
    if (file.size > maxFileSize) return false;
    // Validate file type
    if (
      !acceptedFileTypes.includes(file.type) &&
      !acceptedFileTypes.includes('*')
    )
      return false;
    return true;
  };

  if (isMultipleFiles) {
    // Schema for multiple files (array)
    const baseFileArraySchema = z
      .array(z.instanceof(File, { message: 'Please select a file.' }))
      .refine(
        (files) => {
          // If required and no files provided, invalid
          if (required && (!files || files.length === 0)) return false;
          // If no files provided and not required, check minFiles
          if (!files || files.length === 0) {
            return minFiles === 0 || !required;
          }

          // Validate number of files
          if (minFiles !== undefined && files.length < minFiles) return false;
          if (maxFiles !== undefined && files.length > maxFiles) return false;

          return true;
        },
        {
          message: required
            ? minFiles !== undefined && maxFiles !== undefined
              ? `Please select between ${minFiles} and ${maxFiles} file${maxFiles > 1 ? 's' : ''}.`
              : minFiles !== undefined
                ? `Please select at least ${minFiles} file${minFiles > 1 ? 's' : ''}.`
                : maxFiles !== undefined
                  ? `Please select at most ${maxFiles} file${maxFiles > 1 ? 's' : ''}.`
                  : 'Please select a file.'
            : minFiles !== undefined && maxFiles !== undefined
              ? `Please select between ${minFiles} and ${maxFiles} file${maxFiles > 1 ? 's' : ''}.`
              : minFiles !== undefined
                ? `Please select at least ${minFiles} file${minFiles > 1 ? 's' : ''}.`
                : maxFiles !== undefined
                  ? `Please select at most ${maxFiles} file${maxFiles > 1 ? 's' : ''}.`
                  : 'Please select a file.',
        },
      )
      .refine(
        (files) => {
          if (!files || files.length === 0) return !required;
          // Validate each file
          return files.every((file) => validateFile(file));
        },
        {
          message: `Please upload valid files (max size: ${formatBytes(maxFileSize)}).`,
        },
      );

    if (!required && minFiles === 0) {
      return baseFileArraySchema.nullable().optional();
    }

    return baseFileArraySchema;
  } else {
    // Schema for single file
    const baseFileSchema = z
      .union([
        z.instanceof(File, {
          message: 'Please select a file.',
        }),
        z.null(),
        z.undefined(),
      ])
      .refine(
        (file) => {
          // If required and no file provided, invalid
          if (required && !file) return false;
          // If no file is provided and not required, it's valid
          if (!file) return true;

          // Validate file size
          return file.size <= maxFileSize;
        },
        {
          message: required
            ? 'Please select a file.'
            : `The file is too large. Please choose a file smaller than ${formatBytes(maxFileSize)}.`,
        },
      )
      .refine(
        (file) => {
          // If required and no file provided, invalid
          if (required && !file) return false;
          // If no file is provided and not required, it's valid
          if (!file) return true;

          // Validate file type
          return (
            acceptedFileTypes.includes(file.type) ||
            acceptedFileTypes.includes('*')
          );
        },
        {
          message: required
            ? 'Please select a file.'
            : 'Please upload a valid file (PDF, Word, Excel).',
        },
      )
      .refine(
        (file) => {
          // If required and no file provided, invalid
          if (required && !file) return false;
          // If no file is provided and not required, it's valid
          if (!file) return true;

          return new Promise((resolve) => {
            const reader = new FileReader();
            reader.onload = (e) => {
              resolve(true);
            };
            reader.readAsDataURL(file);
          });
        },
        {
          message: required
            ? 'Please select a file.'
            : `Please upload a file of max size ${formatBytes(maxFileSize)}.`,
        },
      );

    if (!required) {
      return baseFileSchema.nullable().optional();
    }

    // For required files, reject null/undefined explicitly
    return baseFileSchema.refine(
      (file) => file !== null && file !== undefined,
      {
        message: 'Please select a file.',
      },
    );
  }
};

// File URL Schema
export const fileUrlSchemaHelper = 'Please enter a valid file URL.';
export const fileUrlSchemaHelperOptional = `${fileUrlSchemaHelper}, optional`;
export const fileUrlSchema = z.string().url({ message: fileUrlSchemaHelper });
